/* X-All product page — three small enhancements over a form that already
   works without them (map: docs/design-maps/pdp.md):

   <xall-buybox>   re-totals when the plan or quantity radios change and syncs
                   the phone sticky bar. Totals are SERVER-RENDERED for every
                   plan × quantity combination (locale-correct money from
                   Liquid) and read from a JSON script tag — this file never
                   does money math or formatting.
   <xall-gallery>  thumbnail strip switches the stage image.
   <xall-pdp-sticky> shows the phone add-to-cart bar once the buy column has
                   scrolled past (IntersectionObserver; phone-only in CSS).

   The plan and quantity choices are native radios named selling_plan /
   quantity, so submission is correct with this file absent. */

class XallBuybox extends HTMLElement {
  connectedCallback() {
    this.totalEl = this.querySelector('[data-x-total]');
    const split = (s) => (s ? s.split('|') : []);
    this.totals = { once: split(this.dataset.xOnce), sub: split(this.dataset.xSub) };
    this.addEventListener('change', this.sync.bind(this));
    const stp = this.querySelector('.x-stp');
    if (stp) {
      const input = stp.querySelector('input[name="quantity"]');
      stp.addEventListener('click', (e) => {
        const b = e.target.closest('button[data-x-step]');
        if (!b || !input) return;
        const min = parseInt(input.min || '1', 10);
        const max = parseInt(input.max || '6', 10);
        const next = (parseInt(input.value, 10) || min) + parseInt(b.dataset.xStep, 10);
        input.value = Math.min(max, Math.max(min, next));
        this.sync();
      });
      this.stepInput = input;
      this.stepButtons = stp.querySelectorAll('button[data-x-step]');
    }
    this.sync();
  }

  mode() {
    const plan = this.querySelector('input[name="selling_plan"]:checked');
    // No plan radios at all = bare one-time mode.
    if (!plan) return 'once';
    return plan.value === '' ? 'once' : 'sub';
  }

  qty() {
    const r = this.querySelector('input[name="quantity"]:checked');
    if (r) return parseInt(r.value, 10) || 1;
    if (this.stepInput) return parseInt(this.stepInput.value, 10) || 1;
    return 1;
  }

  sync() {
    const mode = this.mode();
    const qty = this.qty();
    if (this.stepInput && this.stepButtons) {
      const min = parseInt(this.stepInput.min || '1', 10);
      const max = parseInt(this.stepInput.max || '6', 10);
      this.stepButtons.forEach((b) => {
        const d = parseInt(b.dataset.xStep, 10);
        b.disabled = d < 0 ? qty <= min : qty >= max;
      });
    }
    if (this.totalEl) {
      const t = (this.totals[mode] || [])[qty - 1];
      if (t) this.totalEl.textContent = t;
    }
    // The plan cards state their own figures at the chosen quantity, the
    // design's planPrice rule — including the crossed-out one-time anchor.
    const subT = this.totals.sub[qty - 1];
    const onceT = this.totals.once[qty - 1];
    this.querySelectorAll('[data-x-price="sub"]').forEach((el) => { if (subT) el.textContent = subT; });
    this.querySelectorAll('[data-x-price="once"]').forEach((el) => { if (onceT) el.textContent = onceT; });
    this.querySelectorAll('[data-x-was]').forEach((el) => { if (onceT) el.textContent = onceT; });
    // The sticky bar shows the per-box price for the chosen mode, not the total.
    const stick = document.querySelector('.x-pstick');
    if (stick) {
      const unit = (this.totals[mode] || [])[0];
      const pr = stick.querySelector('[data-x-stick-price]');
      const em = stick.querySelector('[data-x-stick-mode]');
      if (pr && unit) pr.textContent = unit;
      if (em) em.textContent = mode === 'sub' ? 'subscription' : 'one time';
    }
  }
}

class XallGallery extends HTMLElement {
  connectedCallback() {
    this.stage = this.querySelectorAll('.x-pgal__stage img');
    this.thumbs = this.querySelectorAll('.x-pgal__th');
    // Each rail edge fades only while there is content beyond it: right while
    // more remains ahead, left once the start has scrolled away. The desktop
    // vertical column never scrolls horizontally, so neither class applies.
    const rail = this.querySelector('.x-pgal__thumbs');
    if (rail) {
      const syncFade = () => {
        rail.classList.toggle('x-fade-r', rail.scrollWidth - rail.clientWidth - rail.scrollLeft > 8);
        rail.classList.toggle('x-fade-l', rail.scrollLeft > 8);
      };
      rail.addEventListener('scroll', syncFade, { passive: true });
      window.addEventListener('resize', syncFade, { passive: true });
      syncFade();
    }
    this.addEventListener('click', (e) => {
      const th = e.target.closest('.x-pgal__th');
      if (!th) return;
      const i = parseInt(th.dataset.xShot, 10) || 0;
      this.stage.forEach((img, n) => { img.hidden = n !== i; });
      this.thumbs.forEach((t, n) => {
        t.classList.toggle('x-on', n === i);
        t.setAttribute('aria-pressed', n === i ? 'true' : 'false');
      });
    });
  }
}

class XallPdpSticky extends HTMLElement {
  connectedCallback() {
    // Watch the buy box, not the whole column: the bar's job is to restore the
    // add control once it has scrolled out of sight, and the buy box leaves
    // the viewport whatever the page length below it.
    const col = document.querySelector('.x-bbox') || document.querySelector('.x-pcol');
    const bar = this.querySelector('.x-pstick') || this;
    if (!col || !('IntersectionObserver' in window)) return;
    this.io = new IntersectionObserver(([e]) => {
      bar.classList.toggle('x-on', !e.isIntersecting && e.boundingClientRect.top < 0);
    }, { threshold: 0 });
    this.io.observe(col);
  }
  disconnectedCallback() { if (this.io) this.io.disconnect(); }
}

if (!customElements.get('xall-buybox')) customElements.define('xall-buybox', XallBuybox);
if (!customElements.get('xall-gallery')) customElements.define('xall-gallery', XallGallery);
if (!customElements.get('xall-pdp-sticky')) customElements.define('xall-pdp-sticky', XallPdpSticky);
